import { useState } from "react";
import { FormProvider } from "react-hook-form";
import {
  FormField,
  RadioGroup,
  SectionDivider,
  inputCls,
  readOnlyCls,
} from "../../../components";
import {
  CountrySelect,
  ErrorBanner,
  SurveyGrid,
  TocSidebar,
  useScrollSpy,
} from "../shared/formUtils";
import { useKycPrivForm } from "./hooks/useKycPrivForm";
import {
  PEP_OPTIONS,
  EMPLOYMENT_OPTIONS,
  PRIV_PURPOSE_OPTIONS,
  PORTFOLIO_SIZE_OPTIONS,
  ANNUAL_DEPOSITS_OPTIONS,
  PRIV_SOURCE_OF_FUNDS_OPTIONS,
  PRIV_SOURCE_OF_WEALTH_OPTIONS,
  NO_YES_OTHER_OPTIONS,
  CRYPTO_OPTIONS,
  MIFID_INSTRUMENT_ROWS,
  KNOWLEDGE_COLUMNS,
  EXPERIENCE_COLUMNS,
  TRADING_FREQUENCY_COLUMNS,
  PERIOD_COLUMNS,
  DEBT_BURDEN_OPTIONS,
  INCOME_STABILITY_OPTIONS,
  INVESTMENT_HORIZON_OPTIONS,
  INVESTMENT_GOAL_OPTIONS,
  GROSS_ANNUAL_INCOME_OPTIONS,
  LIQUIDITY_BUFFER_OPTIONS,
  NET_WEALTH_OPTIONS,
  RISK_COMFORT_OPTIONS,
  MARKET_DROP_RESPONSE_OPTIONS,
  LIQUIDITY_OPTIONS,
  INVESTMENT_EXPERIENCE_OPTIONS,
  ESG_PREF_OPTIONS,
  ESG_PREF_TYPE_OPTIONS,
} from "./KycPrivForm.options";
import { flattenKycPriv } from "./KycPrivForm.rules";
import { KycPrivFormProps, KycPrivMode } from "./KycPrivForm.types";
import { Button } from "@/components/Button/Button";

// -- TOC configuration -------------------------------------------------------

const KYC_SECTIONS = [
  { id: "kyc-priv-personopplysninger", label: "Personopplysninger" },
  { id: "kyc-priv-kundekontroll", label: "Kundekontroll" },
  { id: "kyc-priv-aktivitet", label: "Forventet aktivitet" },
];

const MIFID_SECTIONS: { id: string; label: string }[] = [
  { id: "kyc-priv-kunnskap", label: "Kunnskap og erfaring" },
  { id: "kyc-priv-finansiell", label: "Finansiell situasjon" },
  { id: "kyc-priv-horisont", label: "Horisont og mål" },
  { id: "kyc-priv-risiko", label: "Risikotoleranse og adferd" },
  { id: "kyc-priv-esg", label: "ESG-preferanse" },
];

function buildSections(mode: KycPrivMode) {
  if (mode === "kyc") return KYC_SECTIONS;
  if (mode === "mifid") return [KYC_SECTIONS[0], ...MIFID_SECTIONS];
  return [...KYC_SECTIONS, ...MIFID_SECTIONS];
}

// -- Main component ----------------------------------------------------------

export function KycPrivForm({
  contact,
  profileData,
  mode = "combined",
  onDocumentCreated,
  onClose,
}: KycPrivFormProps) {
  const methods = useKycPrivForm(contact, profileData, mode);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = methods;

  const pep = watch("pep");
  const amlEmploymentSituation = watch("amlEmploymentSituation");
  const amlPurpose = watch("amlPurposeOfCustomerRelationship");
  const amlSourceOfFunds = watch("amlSourceOfFunds");
  const amlSourceOfWealth = watch("amlSourceOfWealth");
  const amlManagedOnBehalfOfOthers = watch("amlManagedOnBehalfOfOthers");
  const amlTransfersAbroad = watch("amlTransfersAbroad");

  const showKyc = mode !== "mifid";
  const showMifid = mode !== "kyc";

  const mifidEsgPreference = watch("mifidEsgPreference");

  const [hasEducation, setHasEducation] = useState(() => {
    const v = methods.getValues("mifidRelevantExperienceEducation");
    return v !== "" && v !== "Nei";
  });

  const sections = buildSections(mode);
  const tocIds = sections.map((s) => s.id);

  const onSubmit = handleSubmit(
    (data) => {
      onDocumentCreated?.(flattenKycPriv(data, mode));
      onClose?.();
    },
    (errs) => console.error("KycPrivForm validation errors:", errs)
  );

  const { activeId, scrollTo } = useScrollSpy(tocIds);

  return (
    <FormProvider {...methods}>
      <div className="flex gap-6 items-start">
        <TocSidebar sections={sections} activeId={activeId} onNav={scrollTo} />

        <div className="flex-1 min-w-0 pr-1">
          <form onSubmit={onSubmit} className="space-y-4">
            <ErrorBanner errors={errors} />

            {/* -- Personopplysninger -- */}
            <div
              id="kyc-priv-personopplysninger"
              className="scroll-mt-4 space-y-3"
            >
              <SectionDivider title="Personopplysninger" />

              <FormField label="Navn">
                <input readOnly className={readOnlyCls} {...register("personName")} />
              </FormField>

              <FormField label="Personnummer">
                <input readOnly className={readOnlyCls} {...register("personId")} />
              </FormField>

              <FormField label="Adresse">
                <input className={inputCls} {...register("address")} />
              </FormField>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Postnummer">
                  <input className={inputCls} {...register("zip")} />
                </FormField>
                <FormField label="Poststed">
                  <input className={inputCls} {...register("city")} />
                </FormField>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Statsborgerskap">
                  <CountrySelect {...register("nationality")} />
                </FormField>
                <FormField label="Skattemessig bosted">
                  <CountrySelect {...register("taxCountry")} />
                </FormField>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Telefon">
                  <input className={inputCls} {...register("phone")} />
                </FormField>
                <FormField label="E-postadresse">
                  <input className={inputCls} type="email" {...register("email")} />
                </FormField>
              </div>

              {showKyc && (
                <>
                  <FormField label="Er du, eller har du tidligere vaert, en politisk eksponert person (PEP)?">
                    <RadioGroup options={PEP_OPTIONS} {...register("pep")} />
                    {errors.pep && (
                      <p className="text-xs text-red-600 mt-1">{errors.pep.message}</p>
                    )}
                  </FormField>

                  {pep === "other" && (
                    <FormField label="Hvem er politisk eksponert person, og hvilken stilling?">
                      <textarea rows={3} className={inputCls} {...register("pepCustom")} />
                    </FormField>
                  )}
                </>
              )}
            </div>

            {/* -- Kundekontroll -- */}
            {showKyc && (
              <div id="kyc-priv-kundekontroll" className="scroll-mt-4 space-y-3">
                <SectionDivider title="Kundekontroll" />

                <FormField label="Hva er din arbeidssituasjon?">
                  <RadioGroup
                    options={EMPLOYMENT_OPTIONS}
                    vertical
                    {...register("amlEmploymentSituation")}
                  />
                  {errors.amlEmploymentSituation && (
                    <p className="text-xs text-red-600 mt-1">{errors.amlEmploymentSituation.message}</p>
                  )}
                </FormField>

                {amlEmploymentSituation === "other" && (
                  <FormField label="Spesifiser arbeidssituasjon:">
                    <input className={inputCls} {...register("amlEmploymentSituationCustom")} />
                  </FormField>
                )}

                <FormField label="Hva er formålet med kundeforholdet?">
                  <RadioGroup
                    options={PRIV_PURPOSE_OPTIONS}
                    {...register("amlPurposeOfCustomerRelationship")}
                  />
                  {errors.amlPurposeOfCustomerRelationship && (
                    <p className="text-xs text-red-600 mt-1">{errors.amlPurposeOfCustomerRelationship.message}</p>
                  )}
                </FormField>

                {amlPurpose === "other" && (
                  <FormField label="Spesifiser formål:">
                    <input className={inputCls} {...register("amlPurposeOfCustomerRelationshipCustom")} />
                  </FormField>
                )}
              </div>
            )}

            {/* -- Forventet aktivitet og transaksjonsprofil -- */}
            {showKyc && (
              <div id="kyc-priv-aktivitet" className="scroll-mt-4 space-y-3">
                <SectionDivider title="Forventet aktivitet og transaksjonsprofil" />

                <FormField label="Hvor stor vil investert portefølje hos Capguard være i løpet av kundeforholdet?">
                  <RadioGroup
                    options={PORTFOLIO_SIZE_OPTIONS}
                    {...register("amlInvestedPortfolio")}
                  />
                  {errors.amlInvestedPortfolio && (
                    <p className="text-xs text-red-600 mt-1">{errors.amlInvestedPortfolio.message}</p>
                  )}
                </FormField>

                <FormField label="Hvor mange overførsler vil normalt gjøres i løpet av et år?">
                  <RadioGroup
                    options={ANNUAL_DEPOSITS_OPTIONS}
                    {...register("amlAnnualDeposits")}
                  />
                  {errors.amlAnnualDeposits && (
                    <p className="text-xs text-red-600 mt-1">{errors.amlAnnualDeposits.message}</p>
                  )}
                </FormField>

                <FormField label="Hvor vil overføringer normalt komme fra?">
                  <RadioGroup
                    options={PRIV_SOURCE_OF_FUNDS_OPTIONS}
                    vertical
                    {...register("amlSourceOfFunds")}
                  />
                  {errors.amlSourceOfFunds && (
                    <p className="text-xs text-red-600 mt-1">{errors.amlSourceOfFunds.message}</p>
                  )}
                </FormField>

                {amlSourceOfFunds === "other" && (
                  <FormField label="Spesifiser kilde:">
                    <input className={inputCls} {...register("amlSourceOfFundsCustom")} />
                  </FormField>
                )}

                <FormField label="Forvaltes den aktuelle kapitalen på vegne av andre enn deg selv eller nærmeste familie?">
                  <RadioGroup
                    options={NO_YES_OTHER_OPTIONS}
                    {...register("amlManagedOnBehalfOfOthers")}
                  />
                  {errors.amlManagedOnBehalfOfOthers && (
                    <p className="text-xs text-red-600 mt-1">{errors.amlManagedOnBehalfOfOthers.message}</p>
                  )}
                </FormField>

                {amlManagedOnBehalfOfOthers === "other" && (
                  <FormField label="Angi nærmere detaljer:">
                    <input className={inputCls} {...register("amlManagedOnBehalfOfOthersCustom")} />
                  </FormField>
                )}

                <FormField label="Planlegges overføring til eller fra konto utenfor Norge?">
                  <RadioGroup
                    options={NO_YES_OTHER_OPTIONS}
                    {...register("amlTransfersAbroad")}
                  />
                  {errors.amlTransfersAbroad && (
                    <p className="text-xs text-red-600 mt-1">{errors.amlTransfersAbroad.message}</p>
                  )}
                </FormField>

                {amlTransfersAbroad === "other" && (
                  <FormField label="Spesifiser land/formål:">
                    <input className={inputCls} {...register("amlTransfersAbroadCustom")} />
                  </FormField>
                )}

                <FormField label="Formuens opprinnelse?">
                  <RadioGroup
                    options={PRIV_SOURCE_OF_WEALTH_OPTIONS}
                    vertical
                    {...register("amlSourceOfWealth")}
                  />
                </FormField>

                {amlSourceOfWealth === "other" && (
                  <FormField label="Spesifiser:">
                    <input className={inputCls} {...register("amlSourceOfWealthCustom")} />
                  </FormField>
                )}

                <FormField label="Vil noen av overføringene ha sin opprinnelse fra kryptovaluta?">
                  <RadioGroup
                    options={CRYPTO_OPTIONS}
                    {...register("amlCryptoCurrency")}
                  />
                  {errors.amlCryptoCurrency && (
                    <p className="text-xs text-red-600 mt-1">{errors.amlCryptoCurrency.message}</p>
                  )}
                </FormField>
              </div>
            )}

            {/* -- MiFID: Kunnskap og erfaring -- */}
            {showMifid && (
              <div id="kyc-priv-kunnskap" className="scroll-mt-4 space-y-4">
                <SectionDivider title="Kunnskap og erfaring" />

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Kunnskap om finansielle instrumenter:</p>
                  <SurveyGrid
                    rows={MIFID_INSTRUMENT_ROWS.map((r) => ({ label: r.label, field: r.knowledgeField }))}
                    columns={KNOWLEDGE_COLUMNS}
                  />
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Erfaring med finansielle instrumenter:</p>
                  <SurveyGrid
                    rows={MIFID_INSTRUMENT_ROWS.map((r) => ({ label: r.label, field: r.experienceField }))}
                    columns={EXPERIENCE_COLUMNS}
                  />
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Handelsfrekvens:</p>
                  <SurveyGrid
                    rows={MIFID_INSTRUMENT_ROWS.map((r) => ({ label: r.label, field: r.tradingFrequencyField }))}
                    columns={TRADING_FREQUENCY_COLUMNS}
                  />
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Investeringsperiode:</p>
                  <SurveyGrid
                    rows={MIFID_INSTRUMENT_ROWS.map((r) => ({ label: r.label, field: r.periodField }))}
                    columns={PERIOD_COLUMNS}
                  />
                </div>

                <FormField label="Har du relevant erfaring/utdanning innen finans?">
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="radio"
                        checked={hasEducation}
                        onChange={() => {
                          setHasEducation(true);
                          methods.setValue("mifidRelevantExperienceEducation", "");
                        }}
                        className="accent-blue-600 w-3.5 h-3.5"
                      />
                      <span>Ja</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="radio"
                        checked={!hasEducation}
                        onChange={() => {
                          setHasEducation(false);
                          methods.setValue("mifidRelevantExperienceEducation", "Nei");
                        }}
                        className="accent-blue-600 w-3.5 h-3.5"
                      />
                      <span>Nei</span>
                    </label>
                  </div>
                </FormField>

                {hasEducation && (
                  <FormField label="Spesifiser:">
                    <input
                      className={inputCls}
                      {...register("mifidRelevantExperienceEducation")}
                    />
                  </FormField>
                )}
              </div>
            )}

            {/* -- MiFID: Finansiell situasjon -- */}
            {showMifid && (
              <div id="kyc-priv-finansiell" className="scroll-mt-4 space-y-3">
                <SectionDivider title="Finansiell situasjon" />

                <SurveyGrid
                  rows={[
                    { label: "Total netto formue", field: "mifidTotalNetWealth" },
                    { label: "Investerbar likvid formue", field: "mifidLiquidInvestableWealth" },
                  ]}
                  columns={NET_WEALTH_OPTIONS}
                />

                <FormField label="Brutto årsinntekt">
                  <RadioGroup
                    options={GROSS_ANNUAL_INCOME_OPTIONS}
                    {...register("mifidGrossAnnualIncome")}
                  />
                </FormField>

                <FormField label="Likviditetsbuffer">
                  <RadioGroup
                    options={LIQUIDITY_BUFFER_OPTIONS}
                    {...register("mifidLiquidityBuffer")}
                  />
                </FormField>

                <FormField label="Gjeldsbelastning">
                  <RadioGroup
                    options={DEBT_BURDEN_OPTIONS}
                    {...register("mifidDebtBurden")}
                  />
                </FormField>

                <FormField label="Inntektsstabilitet">
                  <RadioGroup
                    options={INCOME_STABILITY_OPTIONS}
                    vertical
                    {...register("mifidIncomeStability")}
                  />
                  {errors.mifidIncomeStability && (
                    <p className="text-xs text-red-600 mt-1">{errors.mifidIncomeStability.message}</p>
                  )}
                </FormField>
              </div>
            )}

            {/* -- MiFID: Horisont og mål -- */}
            {showMifid && (
              <div id="kyc-priv-horisont" className="scroll-mt-4 space-y-3">
                <SectionDivider title="Horisont og mål" />

                <FormField label="Når forventer du tidligst å ha behov for å ta ut en vesentlig del av denne investeringen/porteføljen?">
                  <RadioGroup
                    options={INVESTMENT_HORIZON_OPTIONS}
                    vertical
                    {...register("mifidEarliestWithdrawal")}
                  />
                  {errors.mifidEarliestWithdrawal && (
                    <p className="text-xs text-red-600 mt-1">{errors.mifidEarliestWithdrawal.message}</p>
                  )}
                </FormField>

                <FormField label="Hva er hovedmålet med investeringen?">
                  <RadioGroup
                    options={INVESTMENT_GOAL_OPTIONS}
                    {...register("mifidMainInvestmentPurpose")}
                  />
                  {errors.mifidMainInvestmentPurpose && (
                    <p className="text-xs text-red-600 mt-1">{errors.mifidMainInvestmentPurpose.message}</p>
                  )}
                </FormField>
              </div>
            )}

            {/* -- MiFID: Risikotoleranse og adferd -- */}
            {showMifid && (
              <div id="kyc-priv-risiko" className="scroll-mt-4 space-y-3">
                <SectionDivider title="Risikotoleranse og adferd" />

                <FormField label="Risikotoleranse: Hvor komfortabel er du med svingninger i porteføljens verdi?">
                  <RadioGroup
                    options={RISK_COMFORT_OPTIONS}
                    {...register("mifidRiskToleranceVolatility")}
                  />
                  {errors.mifidRiskToleranceVolatility && (
                    <p className="text-xs text-red-600 mt-1">{errors.mifidRiskToleranceVolatility.message}</p>
                  )}
                </FormField>

                <FormField label="Risikotoleranse: Hvordan ville du håndtere et markedsfall på 30 % i porteføljen?">
                  <RadioGroup
                    options={MARKET_DROP_RESPONSE_OPTIONS}
                    {...register("mifidMarketDropResponse")}
                  />
                  {errors.mifidMarketDropResponse && (
                    <p className="text-xs text-red-600 mt-1">{errors.mifidMarketDropResponse.message}</p>
                  )}
                </FormField>

                <FormField label="Likviditetsbehov: Hvor viktig er det for deg å kunne trekke ut hele eller store deler av investeringen uten vesentlig verditap på kort varsel?">
                  <RadioGroup
                    options={LIQUIDITY_OPTIONS}
                    {...register("mifidLiquidityNeed")}
                  />
                  {errors.mifidLiquidityNeed && (
                    <p className="text-xs text-red-600 mt-1">{errors.mifidLiquidityNeed.message}</p>
                  )}
                </FormField>

                <FormField label="Hvis din samlede investeringsportefølje faller med 20–30 %, hvilken praktisk konsekvens vil det ha for deg?">
                  <RadioGroup
                    options={INVESTMENT_EXPERIENCE_OPTIONS}
                    {...register("mifidValueDeclineImpact")}
                  />
                  {errors.mifidValueDeclineImpact && (
                    <p className="text-xs text-red-600 mt-1">{errors.mifidValueDeclineImpact.message}</p>
                  )}
                </FormField>
              </div>
            )}

            {/* -- MiFID: ESG-preferanse -- */}
            {showMifid && (
              <div id="kyc-priv-esg" className="scroll-mt-4 space-y-3">
                <SectionDivider title="ESG-preferanse" />

                <FormField label="Har du en egen bærekraftspreferanse som skal hensyntas ved valg av finansielle instrumenter?">
                  <RadioGroup
                    options={ESG_PREF_OPTIONS}
                    {...register("mifidEsgPreference")}
                  />
                  {errors.mifidEsgPreference && (
                    <p className="text-xs text-red-600 mt-1">{errors.mifidEsgPreference.message}</p>
                  )}
                </FormField>

                {mifidEsgPreference === "Ja" && (
                  <FormField label="Gjelder dette preferanse 1, 2, 3 eller 4?">
                    <RadioGroup
                      options={ESG_PREF_TYPE_OPTIONS}
                      vertical
                      {...register("mifidEsgPreferenceType")}
                    />
                  </FormField>
                )}
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button type="button" variant="outline" size="md" onClick={onClose}>
                Avbryt
              </Button>
              <Button type="submit" size="md">
                Lagre
              </Button>
            </div>
          </form>
        </div>
      </div>
    </FormProvider>
  );
}
